import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import api from '../utils/axiosConfig';

const FLAG_BASE = 'https://purecatamphetamine.github.io/country-flag-icons/3x2';

function CountryFlag({ code, className }) {
  return (
    <img
      src={`${FLAG_BASE}/${code}.svg`}
      alt=""
      className={cn('h-4 w-5 object-cover rounded-sm shrink-0', className)}
      loading="lazy"
    />
  );
}

export default function CountrySelect({ value, onChange, placeholder = 'Select country', required, disabled, className, popoverClassName }) {
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCountries = useCallback(async () => {
    try {
      const res = await api.get('/api/countries');
      setCountries(res.data.countries || []);
    } catch (err) {
      console.error('Failed to fetch countries:', err);
      setCountries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const selected = countries.find((c) => String(c.id) === String(value));
  const filtered = search.trim()
    ? countries.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : countries;

  const handleSelect = (country) => {
    onChange?.(country.id);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal h-10',
            !selected && 'text-muted-foreground',
            className
          )}
        >
          <span className="flex items-center gap-2 truncate">
            {selected ? (
              <>
                <CountryFlag code={selected.code} />
                {selected.name}
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[var(--radix-popover-trigger-width)] p-0", popoverClassName)} align="start">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search countries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[240px] overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No countries found</div>
          ) : (
            <div className="p-1">
              {filtered.map((country) => (
                <button
                  key={country.id}
                  type="button"
                  onClick={() => handleSelect(country)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md text-left hover:bg-accent hover:text-accent-foreground',
                    selected?.id === country.id && 'bg-accent'
                  )}
                >
                  <CountryFlag code={country.code} />
                  <span className="truncate">{country.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
